import { ActionIcon, Menu, Radio, Switch, Tooltip } from '@mantine/core';
import { variants } from '@uag/react-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { HeatmapColorMap } from 'app/modules/wallforming/components/content-container/heatmap/types/heatmapColorMap';
import type { HeatmapMetric } from 'app/modules/wallforming/components/content-container/heatmap/types/heatmapMetric';

const metricLabels: Record<HeatmapMetric, string> = {
    overall: 'Overall',
    price: 'Price',
    co2: 'CO2',
    ppm: 'PPM'
};

const colorMapLabels: Record<HeatmapColorMap, string> = {
    classic: 'Temperature',
    greenRed: 'Green → Red',
    blueRed: 'Blue → Red',
    heat: 'Heat',
    mono: 'Mono',
    redIntensity: 'Red Intensity',
    original: 'Original'
};

export const HeatmapOptions = () => {
    const { t } = useTranslation();
    const [menuOpened, setMenuOpened] = useState<boolean>(false);

    const {
        showHeatmapOverlay,
        setShowHeatmapOverlay,
        heatmapMetric,
        setHeatmapMetric,
        heatmapColorMap,
        setHeatmapColorMap
    } = usePlacementStore(
        useShallow((state) => ({
            showHeatmapOverlay: state.showHeatmapOverlay,
            setShowHeatmapOverlay: state.setShowHeatmapOverlay,
            heatmapMetric: state.heatmapMetric,
            setHeatmapMetric: state.setHeatmapMetric,
            heatmapColorMap: state.heatmapColorMap,
            setHeatmapColorMap: state.setHeatmapColorMap
        }))
    );

    return (
        <div className="flex shrink-0 items-center justify-center px-1 sm:px-1">
            <span className="scene-status-action-pair">
                <Tooltip
                    label={showHeatmapOverlay ? 'Disable heatmap' : 'Enable heatmap'}
                    position="bottom"
                    variant={variants.tooltip.bottom}
                >
                    <ActionIcon
                        aria-label={showHeatmapOverlay ? 'Disable heatmap' : 'Enable heatmap'}
                        className="bg-background-transparent hover:bg-background-gray! pointer-events-auto action-icon-medium scene-status-action-button-paired-left"
                        variant={showHeatmapOverlay ? variants.actionIcon.activated : undefined}
                        onPointerDown={() => setShowHeatmapOverlay(!showHeatmapOverlay)}
                    >
                        whatshot
                    </ActionIcon>
                </Tooltip>

                <Menu
                    closeOnItemClick={false}
                    offset={4}
                    opened={menuOpened}
                    position="top-start"
                    shadow="md"
                    withinPortal
                    onChange={setMenuOpened}
                >
                    <Menu.Target>
                        <Tooltip label="Heatmap options" position="bottom" variant={variants.tooltip.bottom}>
                            <ActionIcon
                                aria-label="Heatmap options"
                                className="bg-background-transparent hover:bg-background-gray! pointer-events-auto action-icon-medium scene-status-action-button-compact scene-status-action-button-paired-right leading-none"
                            >
                                {menuOpened ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                            </ActionIcon>
                        </Tooltip>
                    </Menu.Target>

                    <Menu.Dropdown>
                        <Menu.Label className="min-w-60">Heatmap Options</Menu.Label>
                        <Menu.Divider />

                        <span className="m-2">
                            {/* Heatmap visibility toggle */}
                            <Switch
                                checked={showHeatmapOverlay}
                                label={t('labelShowHeatmap')}
                                size="xs"
                                onChange={() => setShowHeatmapOverlay(!showHeatmapOverlay)}
                            />

                            {/* Heatmap data selection */}
                            <div className="text-xs font-medium text-text-light uppercase mt-5">{t('data')}</div>
                            <div className="pl-2">
                                <Radio.Group
                                    value={heatmapMetric}
                                    onChange={(value) => setHeatmapMetric(value as HeatmapMetric)}
                                >
                                    {Object.entries(metricLabels).map(([value, label]) => (
                                        <Radio key={value} label={label} value={value} />
                                    ))}
                                </Radio.Group>
                            </div>

                            {/* Heatmap color mode */}
                            <div className="text-xs font-medium text-text-light uppercase mt-5">
                                {t('labelHeatmapColorMode')}
                            </div>
                            <div className="pl-2">
                                <Radio.Group
                                    value={heatmapColorMap}
                                    onChange={(value) => setHeatmapColorMap(value as HeatmapColorMap)}
                                >
                                    {Object.entries(colorMapLabels).map(([value, label]) => (
                                        <Radio key={value} label={label} value={value} />
                                    ))}
                                </Radio.Group>
                            </div>
                        </span>
                    </Menu.Dropdown>
                </Menu>
            </span>
        </div>
    );
};
